import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Clapperboard,
    Film,
    ImagePlus,
    LayoutTemplate,
    Loader2,
    Music,
    Sparkles,
    Type,
    Upload,
    Plus,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { CsAsset } from '../../../../common/models/content-studio.model';
import { DroppedMedia, MEDIA_DRAG_TYPE } from './Timeline';
import { TEXT_PRESETS, TextPresetId, VideoBrand } from './presets';
import { TEMPLATES, TemplateId } from './templates';
import { cssFontStack, requestFontFamily } from './fonts';
import { probeAudio, probeVideo, useVideoPoster } from './media';
import { PanelSection } from './controls';

type Tab = 'media' | 'text' | 'audio' | 'templates';

export type ShapeKind = 'scrim-bottom' | 'scrim-top' | 'rect' | 'line';

interface MediaBinProps {
    projectUuid: string;
    brand: VideoBrand;
    videoDuration: number;
    onAddMedia: (media: DroppedMedia) => void;
    onAddPreset: (id: TextPresetId) => void;
    onAddShape: (shape: ShapeKind) => void;
    onAddLogo: () => void;
    onAddAudio: (media: DroppedMedia) => void;
    onApplyTemplate: (id: TemplateId) => void;
    /** Shown in the Templates tab: what the template will be built from. */
    templateSourceCount: number;
    /** Add a plain colour card (title / end frame) to the main track. */
    onAddColor?: (color: string) => void;
}

/** Card colours offered before the brand's own: near-black, paper, warm neutrals. */
const CARD_NEUTRALS = ['#0E0E0C', '#FFFFFF', '#F4F1EB', '#D6CFC4', '#3A3733'];

export const MOODS = ['WARM_ACOUSTIC', 'MINIMAL_PIANO', 'LOFI', 'CINEMATIC_AMBIENT', 'UPBEAT_HOUSE', 'SOFT_JAZZ'] as const;

function assetToMedia(a: CsAsset): DroppedMedia {
    return {
        kind: a.kind === 'video' ? 'video' : 'image',
        src: a.url!,
        assetUuid: a.uuid,
        thumbnailUrl: a.thumbnailUrl || (a.kind === 'image' ? a.url : undefined),
        durationSec: a.dimensions?.durationSec,
        aspect: a.dimensions?.width && a.dimensions?.height ? a.dimensions.width / a.dimensions.height : undefined,
    };
}

/** First frame of a local video file as a PNG, for the uploaded clip's poster. */
async function posterOf(file: File): Promise<{ blob: Blob | null; width: number; height: number; duration: number }> {
    const url = URL.createObjectURL(file);
    try {
        const el = document.createElement('video');
        el.muted = true;
        el.preload = 'auto';
        el.src = url;
        await new Promise<void>((resolve, reject) => {
            el.onloadeddata = () => resolve();
            el.onerror = () => reject(new Error('unreadable'));
        });
        el.currentTime = Math.min(0.2, (el.duration || 1) / 3);
        await new Promise<void>((resolve) => { el.onseeked = () => resolve(); setTimeout(resolve, 3000); });
        const c = document.createElement('canvas');
        c.width = el.videoWidth;
        c.height = el.videoHeight;
        c.getContext('2d')?.drawImage(el, 0, 0);
        const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/png'));
        return { blob, width: el.videoWidth, height: el.videoHeight, duration: el.duration };
    } finally {
        URL.revokeObjectURL(url);
    }
}

export const MediaBin: React.FC<MediaBinProps> = ({
    projectUuid,
    brand,
    videoDuration,
    onAddMedia,
    onAddPreset,
    onAddShape,
    onAddLogo,
    onAddAudio,
    onApplyTemplate,
    templateSourceCount,
    onAddColor,
}) => {
    const { t } = useTranslation();
    const api = useContentStudioApi();
    const [tab, setTab] = useState<Tab>('media');
    const [assets, setAssets] = useState<CsAsset[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'video' | 'image'>('all');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const audioRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        try {
            const res = await api.listAssets({ projectUuid, limit: 200 });
            setAssets((res.items || []).filter((a) => a.status === 'ready' && a.url && (a.kind === 'image' || a.kind === 'video')));
        } catch {
            /* the bin stays as it was */
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectUuid]);

    useEffect(() => { void load(); }, [load]);

    // Warm the preset fonts so the gallery tiles show their real faces.
    useEffect(() => {
        if (tab !== 'text') return;
        Array.from(new Set(TEXT_PRESETS.map((p) => p.sample.fontFamily))).forEach((f) => void requestFontFamily(f));
    }, [tab]);

    const uploadMedia = async (files: FileList | null) => {
        if (!files?.length) return;
        setUploading(true);
        setError(null);
        try {
            for (const file of Array.from(files)) {
                if (file.type.startsWith('video/')) {
                    const poster = await posterOf(file).catch(() => null);
                    await api.importVideo(projectUuid, file, {
                        thumbnail: poster?.blob,
                        width: poster?.width,
                        height: poster?.height,
                        durationSec: poster?.duration,
                        origin: 'upload',
                    });
                } else if (file.type.startsWith('image/')) {
                    await api.importImage(projectUuid, { file });
                }
            }
            await load();
        } catch (err: any) {
            setError(t('VIDEO_EDITOR.BIN.UPLOAD_FAILED'));
            // eslint-disable-next-line no-console
            console.error('[video-editor] upload failed', err);
        } finally {
            setUploading(false);
        }
    };

    const uploadAudio = async (files: FileList | null) => {
        const file = files?.[0];
        if (!file) return;
        setUploading(true);
        setError(null);
        try {
            const { url } = await api.uploadStyleRef(file);
            const duration = await probeAudio(url);
            onAddAudio({ kind: 'audio', src: url, durationSec: duration ?? undefined, name: file.name.replace(/\.[^.]+$/, '') });
        } catch (err) {
            setError(t('VIDEO_EDITOR.BIN.UPLOAD_FAILED'));
            // eslint-disable-next-line no-console
            console.error('[video-editor] audio upload failed', err);
        } finally {
            setUploading(false);
        }
    };

    const visible = assets.filter((a) => filter === 'all' || a.kind === filter);

    const tabs: Array<{ id: Tab; icon: React.ReactNode; label: string }> = [
        { id: 'media', icon: <Clapperboard />, label: t('VIDEO_EDITOR.BIN.TAB_MEDIA') },
        { id: 'text', icon: <Type />, label: t('VIDEO_EDITOR.BIN.TAB_TEXT') },
        { id: 'audio', icon: <Music />, label: t('VIDEO_EDITOR.BIN.TAB_AUDIO') },
        { id: 'templates', icon: <LayoutTemplate />, label: t('VIDEO_EDITOR.BIN.TAB_TEMPLATES') },
    ];

    return (
        <div className="flex h-full min-h-0 border-e bg-background">
            <nav className="flex w-16 shrink-0 flex-col items-center gap-1 border-e py-2">
                {tabs.map((tb) => (
                    <button
                        key={tb.id}
                        type="button"
                        onClick={() => setTab(tb.id)}
                        aria-pressed={tab === tb.id}
                        className={cn(
                            'flex w-14 flex-col items-center gap-1 rounded-lg py-2 text-[10px] transition-colors [&_svg]:size-[18px]',
                            tab === tb.id ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                        )}
                    >
                        {tb.icon}
                        {tb.label}
                    </button>
                ))}
            </nav>

            <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
                {tab === 'media' && (
                    <PanelSection
                        title={t('VIDEO_EDITOR.BIN.PROJECT_MEDIA')}
                        action={(
                            <Button type="button" size="xs" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                                {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
                                {t('VIDEO_EDITOR.BIN.UPLOAD')}
                            </Button>
                        )}
                    >
                        <input
                            ref={fileRef}
                            type="file"
                            accept="video/mp4,video/quicktime,video/webm,image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => { void uploadMedia(e.target.files); e.target.value = ''; }}
                        />
                        <div className="flex gap-1">
                            {(['all', 'video', 'image'] as const).map((f) => (
                                <button
                                    key={f}
                                    type="button"
                                    onClick={() => setFilter(f)}
                                    className={cn(
                                        'rounded-md border px-2 py-0.5 text-[11px] transition-colors',
                                        filter === f ? 'border-foreground text-foreground' : 'border-border text-muted-foreground hover:bg-muted',
                                    )}
                                >
                                    {t(`VIDEO_EDITOR.BIN.FILTER_${f.toUpperCase()}`)}
                                </button>
                            ))}
                        </div>
                        {error && <p className="text-xs text-destructive">{error}</p>}
                        {loading ? (
                            <div className="flex justify-center py-8 text-muted-foreground"><Loader2 className="size-4 animate-spin" /></div>
                        ) : visible.length === 0 ? (
                            <p className="py-6 text-center text-xs text-muted-foreground">{t('VIDEO_EDITOR.BIN.EMPTY')}</p>
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                {visible.map((a) => {
                                    const media = assetToMedia(a);
                                    return (
                                        <button
                                            key={a.uuid}
                                            type="button"
                                            draggable
                                            onDragStart={(e) => {
                                                e.dataTransfer.setData(MEDIA_DRAG_TYPE, JSON.stringify(media));
                                                e.dataTransfer.effectAllowed = 'copy';
                                            }}
                                            onClick={() => onAddMedia(media)}
                                            title={t('VIDEO_EDITOR.BIN.ADD_HINT')}
                                            className="group relative aspect-square overflow-hidden rounded-lg border bg-muted"
                                            data-ve-bin-item={a.uuid}
                                        >
                                            <BinPoster media={media} />
                                            {a.kind === 'video' && (
                                                <span className="absolute bottom-1 start-1 flex items-center gap-1 rounded bg-black/60 px-1 text-[10px] tabular-nums text-white">
                                                    <Film className="size-2.5" />
                                                    {a.dimensions?.durationSec ? `${a.dimensions.durationSec.toFixed(1)}s` : ''}
                                                </span>
                                            )}
                                            <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-white opacity-0 transition-all group-hover:bg-black/25 group-hover:opacity-100">
                                                <Plus className="size-5" />
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </PanelSection>
                )}

                {tab === 'media' && onAddColor && (
                    <PanelSection title={t('VIDEO_EDITOR.BIN.COLOR_CARDS')}>
                        <p className="text-xs text-muted-foreground">{t('VIDEO_EDITOR.BIN.COLOR_CARDS_HINT')}</p>
                        <div className="grid grid-cols-5 gap-1.5">
                            {Array.from(new Set([...(brand.ground ? [brand.ground] : []), ...CARD_NEUTRALS, ...(brand.palette || [])].map((c) => c.toUpperCase())))
                                .slice(0, 10)
                                .map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        title={c}
                                        aria-label={`${t('VIDEO_EDITOR.BIN.COLOR_CARDS')} ${c}`}
                                        onClick={() => onAddColor(c)}
                                        className="aspect-[9/16] rounded-md border border-border transition-shadow hover:ring-[1.5px] hover:ring-foreground hover:ring-offset-1 hover:ring-offset-background"
                                        style={{ background: c }}
                                        data-ve-color-card={c}
                                    />
                                ))}
                        </div>
                    </PanelSection>
                )}

                {tab === 'text' && (
                    <>
                        <PanelSection title={t('VIDEO_EDITOR.BIN.TEXT_STYLES')}>
                            <div className="grid grid-cols-2 gap-2">
                                {TEXT_PRESETS.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => onAddPreset(p.id)}
                                        className="flex aspect-[4/3] flex-col items-stretch overflow-hidden rounded-lg border bg-stone-900 text-left transition-colors hover:border-foreground"
                                        data-ve-preset={p.id}
                                    >
                                        <span className="flex min-h-0 flex-1 items-center justify-center overflow-hidden px-2 text-center text-white">
                                            <span
                                                className={cn('leading-tight', p.sample.pill && 'rounded-full bg-stone-100 px-2 py-0.5 text-stone-900')}
                                                style={{
                                                    fontFamily: cssFontStack(p.sample.fontFamily),
                                                    fontWeight: p.sample.fontWeight,
                                                    fontStyle: p.sample.fontStyle,
                                                    textTransform: p.sample.uppercase ? 'uppercase' : undefined,
                                                    letterSpacing: p.sample.letterSpacing ? `${p.sample.letterSpacing}em` : undefined,
                                                    fontSize: p.sample.size,
                                                }}
                                            >
                                                {t(p.sample.textKey)}
                                            </span>
                                        </span>
                                        <span className="border-t border-white/10 bg-stone-950 px-2 py-1 text-[10px] text-stone-300">{t(p.nameKey)}</span>
                                    </button>
                                ))}
                            </div>
                        </PanelSection>
                        <PanelSection title={t('VIDEO_EDITOR.BIN.SHAPES')}>
                            <div className="grid grid-cols-2 gap-2">
                                {(['scrim-bottom', 'scrim-top', 'rect', 'line'] as ShapeKind[]).map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => onAddShape(s)}
                                        className="flex flex-col items-center gap-1.5 rounded-lg border px-2 py-2.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        <ShapeGlyph kind={s} />
                                        {t(`VIDEO_EDITOR.BIN.SHAPE_${s.replace('-', '_').toUpperCase()}`)}
                                    </button>
                                ))}
                            </div>
                        </PanelSection>
                        <PanelSection title={t('VIDEO_EDITOR.BIN.BRAND')}>
                            {brand.logoUrl ? (
                                <button
                                    type="button"
                                    onClick={onAddLogo}
                                    className="flex items-center gap-3 rounded-lg border p-2 text-start text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                >
                                    <span className="flex size-10 items-center justify-center rounded-md bg-muted bg-contain bg-center bg-no-repeat" style={{ backgroundImage: `url(${brand.logoUrl})` }} />
                                    {t('VIDEO_EDITOR.BIN.ADD_LOGO')}
                                </button>
                            ) : (
                                <p className="text-xs text-muted-foreground">{t('VIDEO_EDITOR.BIN.NO_LOGO')}</p>
                            )}
                        </PanelSection>
                    </>
                )}

                {tab === 'audio' && (
                    <>
                        <PanelSection title={t('VIDEO_EDITOR.BIN.MUSIC_UPLOAD')}>
                            <input
                                ref={audioRef}
                                type="file"
                                accept="audio/*"
                                className="hidden"
                                onChange={(e) => { void uploadAudio(e.target.files); e.target.value = ''; }}
                            />
                            <Button type="button" variant="outline" onClick={() => audioRef.current?.click()} disabled={uploading}>
                                {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
                                {t('VIDEO_EDITOR.BIN.UPLOAD_AUDIO')}
                            </Button>
                            {error && <p className="text-xs text-destructive">{error}</p>}
                        </PanelSection>
                        <MusicGenerator videoDuration={videoDuration} onReady={onAddAudio} />
                    </>
                )}

                {tab === 'templates' && (
                    <PanelSection title={t('VIDEO_EDITOR.BIN.TEMPLATES')}>
                        <p className="text-xs text-muted-foreground">
                            {t('VIDEO_EDITOR.BIN.TEMPLATES_HINT', { count: templateSourceCount })}
                        </p>
                        <div className="flex flex-col gap-2">
                            {TEMPLATES.map((tpl) => (
                                <button
                                    key={tpl.id}
                                    type="button"
                                    disabled={templateSourceCount < tpl.minMedia}
                                    onClick={() => onApplyTemplate(tpl.id)}
                                    className="flex items-start gap-3 rounded-lg border p-3 text-start transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                                    data-ve-template={tpl.id}
                                >
                                    <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border bg-muted text-muted-foreground [&_svg]:size-4">
                                        <TemplateGlyph id={tpl.id} />
                                    </span>
                                    <span className="flex flex-col gap-0.5">
                                        <span className="text-sm font-medium text-foreground">{t(tpl.nameKey)}</span>
                                        <span className="text-xs text-muted-foreground">{t(tpl.descriptionKey)}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                        <p className="text-[11px] text-muted-foreground">{t('VIDEO_EDITOR.BIN.TEMPLATES_UNDO')}</p>
                    </PanelSection>
                )}
            </div>
        </div>
    );
};

const MusicGenerator: React.FC<{ videoDuration: number; onReady: (m: DroppedMedia) => void }> = ({ videoDuration, onReady }) => {
    const { t } = useTranslation();
    const api = useContentStudioApi();
    const [prompt, setPrompt] = useState('');
    const [mood, setMood] = useState<typeof MOODS[number] | null>('WARM_ACOUSTIC');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const generate = async () => {
        const moodText = mood ? t(`VIDEO_EDITOR.MOOD.${mood}`) : '';
        const text = [moodText, prompt.trim()].filter(Boolean).join(', ');
        if (!text) return;
        setBusy(true);
        setError(null);
        try {
            const durationSec = Math.max(5, Math.ceil(videoDuration || 15));
            const res = await api.generateMusic({ prompt: text, durationSec });
            onReady({ kind: 'audio', src: res.url, durationSec: res.durationSec ?? durationSec, name: moodText || t('VIDEO_EDITOR.BIN.GENERATED_MUSIC') });
        } catch (err: any) {
            const status = err?.response?.status ?? err?.status;
            setError(status === 404 || status === 501
                ? t('VIDEO_EDITOR.BIN.MUSIC_UNAVAILABLE')
                : t('VIDEO_EDITOR.BIN.MUSIC_FAILED'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <PanelSection title={t('VIDEO_EDITOR.BIN.GENERATE_MUSIC')}>
            <div className="flex flex-wrap gap-1.5">
                {MOODS.map((m) => (
                    <button
                        key={m}
                        type="button"
                        onClick={() => setMood(mood === m ? null : m)}
                        aria-pressed={mood === m}
                        className={cn(
                            'rounded-full border px-2.5 py-1 text-[11px] transition-colors',
                            mood === m ? 'border-foreground text-foreground' : 'border-border text-muted-foreground hover:bg-muted',
                        )}
                    >
                        {t(`VIDEO_EDITOR.MOOD.${m}`)}
                    </button>
                ))}
            </div>
            <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('VIDEO_EDITOR.BIN.MUSIC_PLACEHOLDER')}
                className="min-h-[72px] text-sm"
            />
            <Button type="button" onClick={generate} disabled={busy || (!prompt.trim() && !mood)}>
                {busy ? <Loader2 className="animate-spin" /> : <Sparkles />}
                {busy ? t('VIDEO_EDITOR.BIN.GENERATING') : t('VIDEO_EDITOR.BIN.GENERATE')}
            </Button>
            <p className="text-[11px] text-muted-foreground">
                {t('VIDEO_EDITOR.BIN.MUSIC_LENGTH', { seconds: Math.max(5, Math.ceil(videoDuration || 15)) })}
            </p>
            {error && <p className="text-xs text-destructive">{error}</p>}
        </PanelSection>
    );
};

const BinPoster: React.FC<{ media: DroppedMedia }> = ({ media }) => {
    const poster = useVideoPoster(media.src, media.thumbnailUrl, media.kind === 'video');
    return poster
        ? <span className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${poster})` }} />
        : <span className="absolute inset-0 flex items-center justify-center text-muted-foreground"><Film className="size-5" /></span>;
};

const ShapeGlyph: React.FC<{ kind: ShapeKind }> = ({ kind }) => {
    if (kind === 'scrim-bottom' || kind === 'scrim-top') {
        return (
            <span
                className="h-7 w-10 rounded-[4px] border"
                style={{ background: `linear-gradient(${kind === 'scrim-bottom' ? '180deg' : '0deg'}, transparent, rgba(0,0,0,0.65))` }}
            />
        );
    }
    if (kind === 'line') return <span className="flex h-7 w-10 items-center"><span className="h-px w-full bg-foreground" /></span>;
    return <span className="h-7 w-10 rounded-[4px] bg-foreground/80" />;
};

const TemplateGlyph: React.FC<{ id: TemplateId }> = ({ id }) => {
    if (id === 'texture-reel') return <Sparkles />;
    if (id === 'collection-lineup') return <LayoutTemplate />;
    if (id === 'before-after') return <ImagePlus />;
    return <Film />;
};

export { assetToMedia, probeVideo };
